import { useEffect, useState, type ReactNode } from "react";
import { observer } from "mobx-react-lite";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Compass, History, UserPlus, Users } from "lucide-react";
import { useStore } from "../../stores/rootStore";
import { CreatorSuggestionsStore } from "../../stores/creatorSuggestionsStore";
import { selectStarterGames, type StarterGames } from "../../lib/discoverRecommendations";
import { hive } from "../../lib/apiClient";
import { formatCount } from "../../lib/format";
import { playablePath } from "../../lib/playableKind";
import { playableCoverImageProps } from "../../lib/playableCoverImage";
import { Avatar } from "../ui/Avatar";
import { CoverImage } from "../ui/CoverImage";
import { Skeleton } from "../ui/Skeleton";
import { Spinner } from "../ui/Spinner";
import { DistributionCard, DistributionPreviewProvider } from "./DistributionCard";
import { RetryMessage } from "./ExploreShelves";

function RecommendationIntro({ history = false, children }: { history?: boolean; children?: ReactNode }) {
	const { t } = useTranslation();
	const Icon = history ? History : Users;
	return <div className="discover-empty-intro">
		<div className="discover-empty-icon"><Icon size={25} strokeWidth={1.7} /></div>
		<div className="discover-empty-copy">
			<h2>{t(history ? "discover.historyEmptyTitle" : "discover.followingEmptyTitle")}</h2>
			<p>{t(history ? "discover.historyEmptyDescription" : "discover.followingEmptyDescription")}</p>
		</div>
		{children ?? <Link to="/" className="btn-ghost"><Compass size={16} />{t("discover.explore")}</Link>}
	</div>;
}

function RecommendationSkeleton({ creators = false }: { creators?: boolean }) {
	const { t } = useTranslation();
	return <div role="status" aria-label={t("common.loading")} className={creators ? "discover-creator-grid" : "discover-starter-grid"}>
		{Array.from({ length: creators ? 4 : 6 }, (_, index) => <Skeleton key={index} className={creators ? "h-64 rounded-2xl" : "aspect-[512/772] rounded-xl"} />)}
	</div>;
}

export const FollowingEmptyRecommendations = observer(({ viewerId }: { viewerId: string }) => {
	const { auth, discover, ui } = useStore();
	const { t } = useTranslation();
	const [suggestions] = useState(() => new CreatorSuggestionsStore(viewerId,
		() => auth.user?.id === viewerId && auth.isLoggedIn && !auth.isGuest));
	useEffect(() => { void suggestions.load(); return () => suggestions.cancel(); }, [suggestions]);
	return <section className="discover-empty-recommendations">
		<RecommendationIntro>{suggestions.changed ? <button type="button" className="btn-primary"
			onClick={() => void discover.loadInitial()}>{t("discover.viewFollowing")}<ArrowRight size={16} /></button> : undefined}</RecommendationIntro>
		<div className="discover-recommendation-heading"><div><h2>{t("discover.creatorsToFollow")}</h2><p>{t("discover.creatorsToFollowDescription")}</p></div></div>
		{suggestions.loading ? <RecommendationSkeleton creators /> : suggestions.error ? <RetryMessage onRetry={() => void suggestions.load()} /> : suggestions.items.length ? (
			<div className="discover-creator-grid">
				{suggestions.items.map((user) => <article className="discover-creator-card" key={user.id}>
					<div className="discover-creator-identity">
						<Link to={"/u/" + encodeURIComponent(user.handle)} className="discover-creator-profile">
							<Avatar src={user.avatarUrl} name={user.displayName ?? user.handle} size={46} />
							<div><h3>{user.displayName ?? user.handle}</h3><p>@{user.handle}</p></div>
						</Link>
						<button type="button" className={user.viewer!.isFollowing ? "btn-ghost discover-follow-btn" : "btn-primary discover-follow-btn"}
							disabled={suggestions.busy.has(user.id)} aria-pressed={user.viewer!.isFollowing}
							aria-label={t(user.viewer!.isFollowing ? "discover.unfollowCreator" : "discover.followCreator", { name: user.displayName ?? user.handle })}
							onClick={() => void suggestions.toggle(user).then((result) => { if (result === "failed") ui.toast(t("profile.followFailed"), "error"); })}>
							{suggestions.busy.has(user.id) ? <Spinner size={14} /> : user.viewer!.isFollowing ? <Check size={14} /> : <UserPlus size={14} />}
							{t(user.viewer!.isFollowing ? "profile.followingBtn" : "profile.follow")}
						</button>
					</div>
					<p className="discover-creator-followers">{t("discover.creatorFollowers", { followers: formatCount(user.stats!.followersCount) })}</p>
					{user.topWorks!.length ? <div className="discover-creator-works">
						{user.topWorks!.filter((work) => ["game", "drama"].includes(work.kind)).slice(0, 3).map((work) => <Link key={work.id}
							to={playablePath(work)} aria-label={work.title} title={work.title}>
							<CoverImage {...(work.coverUrl ? playableCoverImageProps(work.coverUrl, "130px") : { src: "/og-default.png" })} alt={work.title}
								className="aspect-[512/772] rounded-lg" fallback={<span className="discover-work-fallback">{work.title}</span>} />
						</Link>)}
					</div> : <Link to={"/u/" + encodeURIComponent(user.handle)} className="discover-creator-visit">{t("discover.visitCreator")}<ArrowRight size={15} /></Link>}
				</article>)}
			</div>
		) : <p className="text-sm text-muted">{t("discover.noCreatorSuggestions")}</p>}
	</section>;
});

export const RecentlyPlayedEmptyRecommendations = observer(() => {
	const { discover, explore } = useStore();
	const { t } = useTranslation();
	const configured = selectStarterGames(discover.tagSections, explore.data?.sections ?? []);
	// Wait for CMS to settle before considering a public Hot fallback.
	const pending = !discover.tagSectionsSettled || explore.loading;
	const needsFallback = !pending && !configured;
	const [fallback, setFallback] = useState<StarterGames | null>(null);
	const [failed, setFailed] = useState(false);
	const [attempt, setAttempt] = useState(0);
	useEffect(() => {
		if (!needsFallback) return;
		let active = true;
		setFailed(false);
		setFallback(null);
		void hive.feed.forYou({ sort: "hot", kind: "game", limit: 6 }).then((page) => {
			if (active) setFallback({ items: page.items.filter((item) => item.kind === "game").slice(0, 6), source: "popular", href: "/?tab=popular" });
		}, () => { if (active) setFailed(true); });
		return () => { active = false; };
	}, [needsFallback, attempt]);
	const games = pending ? null : configured ?? fallback;
	return <section className="discover-empty-recommendations">
		<RecommendationIntro history />
		{pending || (!games && !failed) ? <RecommendationSkeleton /> : failed && !configured ? <RetryMessage onRetry={() => setAttempt((value) => value + 1)} /> : games?.items.length ? <>
			<div className="discover-recommendation-heading">
				<div><h2>{t(games.source === "popular" ? "discover.popular" : games.source === "curated" ? "discover.curatedStarterTitle" : "discover.exploreStarterTitle")}</h2>
					<p>{t("discover.starterDescription")}</p></div>
				<Link to={games.href}>{t("discover.seeAll")}<ArrowRight size={16} /></Link>
			</div>
			<DistributionPreviewProvider hoverOnlyVideo>
				<div className="discover-starter-grid">{games.items.map((item, index) => <DistributionCard key={item.id} item={item}
					coverOnly coverSizes="200px" properties={{ content_id: item.id, content_kind: "game", source: "discover", position: index + 1, feed_tab: "recentlyPlayed", category: games.source }} />)}</div>
			</DistributionPreviewProvider>
		</> : <p className="text-sm text-muted">{t("discover.noStarterSuggestions")}</p>}
	</section>;
});
